import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { UserPlus, ArrowLeft, Upload, MapPin } from "lucide-react";
import { districtFromPostalCode, formatPostalCode } from "@/lib/postalCodes";

function toBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export default function RecommendCandidate() {
  const { vacancyId } = useParams();
  const [vacancy, setVacancy] = useState(null);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    name: "", phone: "", email: "", locality: "", availability: "",
    experience: "", cv_data: null, cv_filename: "", observations: "",
  });
  const [postalCode, setPostalCode] = useState("");
  const suggestedDistrict = districtFromPostalCode(postalCode);
  const navigate = useNavigate();
  const source = new URLSearchParams(window.location.search).get("src") || "direct";

  useEffect(() => {
    api.get(`/vacancies/${vacancyId}`).then(({ data }) => {
      setVacancy(data);
      if (data.status !== "open") {
        toast.error("Esta vaga já foi preenchida e deixou de aceitar recomendações.");
        navigate("/vacancies", { replace: true });
      }
    });
  }, [vacancyId, navigate]);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 4 * 1024 * 1024) { toast.error("CV excede 4 MB"); return; }
    const b64 = await toBase64(file);
    setForm((f) => ({ ...f, cv_data: b64, cv_filename: file.name }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!postalCode || !suggestedDistrict) { toast.error("Indique o código postal (formato NNNN-NNN)"); return; }
    setBusy(true);
    try {
      const payload = {
        ...form,
        locality: form.locality?.trim() ? `${form.locality.trim()} (${suggestedDistrict})` : suggestedDistrict,
        postal_code: postalCode,
        vacancy_id: vacancyId,
        source,
      };
      await api.post("/candidates", payload);
      toast.success("Candidato recomendado com sucesso!");
      navigate("/dashboard");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  if (!vacancy) return <div className="text-[var(--ink-soft)]">A carregar…</div>;

  const inputCls = "w-full px-3 py-2.5 rounded-xl bg-[var(--muted)] border border-[var(--border)] focus:outline-none focus:ring-2 focus:ring-[var(--forest)]";

  return (
    <div className="max-w-3xl">
      <Link to="/vacancies" className="pill-btn pill-ghost !py-2 !px-3 w-fit"><ArrowLeft className="w-4 h-4"/> Voltar</Link>
      <div className="mt-4">
        <div className="eyebrow">Recomendação</div>
        <h1 className="font-heading text-4xl mt-2 flex items-center gap-3"><UserPlus className="w-8 h-8 text-[var(--forest)]"/> Recomendar candidato</h1>
        <p className="text-[var(--ink-soft)] mt-2">Vaga: <strong>{vacancy.title}</strong> · {vacancy.locality}</p>
      </div>

      <form onSubmit={submit} className="card-surface p-8 mt-6 space-y-5">
        <div className="grid md:grid-cols-2 gap-4">
          <div><label className="text-sm font-medium">Nome do candidato</label><input required value={form.name} onChange={update("name")} className={`mt-1.5 ${inputCls}`} data-testid="cand-name-input"/></div>
          <div><label className="text-sm font-medium">Telefone</label><input required value={form.phone} onChange={update("phone")} className={`mt-1.5 ${inputCls}`} data-testid="cand-phone-input"/></div>
          <div><label className="text-sm font-medium">Email</label><input required type="email" value={form.email} onChange={update("email")} className={`mt-1.5 ${inputCls}`} data-testid="cand-email-input"/></div>
          <div>
            <label className="text-sm font-medium">Código postal</label>
            <input
              required
              value={postalCode}
              onChange={(e) => setPostalCode(formatPostalCode(e.target.value))}
              placeholder="1000-100"
              maxLength={8}
              inputMode="numeric"
              className={`mt-1.5 ${inputCls}`}
              data-testid="cand-postal-input"
            />
            {postalCode && suggestedDistrict && (
              <div className="text-xs text-[var(--forest)] mt-1 flex items-center gap-1" data-testid="cand-district-suggestion">
                <MapPin className="w-3 h-3"/> Distrito: <strong>{suggestedDistrict}</strong>
              </div>
            )}
            {postalCode && !suggestedDistrict && postalCode.replace(/\D/g, "").length >= 4 && (
              <div className="text-xs text-[var(--terracotta)] mt-1">Código postal não reconhecido</div>
            )}
          </div>
          <div className="md:col-span-2"><label className="text-sm font-medium">Freguesia / bairro <span className="text-xs text-[var(--ink-soft)]">(opcional)</span></label><input value={form.locality} onChange={update("locality")} placeholder="Ex: Alvalade, Cedofeita…" className={`mt-1.5 ${inputCls}`} data-testid="cand-locality-input"/></div>
          <div className="md:col-span-2"><label className="text-sm font-medium">Disponibilidade</label><input required value={form.availability} onChange={update("availability")} className={`mt-1.5 ${inputCls}`} placeholder="Ex: imediata / a partir de 1 setembro" data-testid="cand-availability-input"/></div>
          <div className="md:col-span-2"><label className="text-sm font-medium">Experiência</label><textarea required value={form.experience} onChange={update("experience")} className={`mt-1.5 ${inputCls} min-h-[80px]`} data-testid="cand-experience-input"/></div>
          <div className="md:col-span-2">
            <label className="text-sm font-medium">CV <span className="text-xs text-[var(--ink-soft)]">(PDF ou imagem, máx 4 MB)</span></label>
            <div className="file-drop mt-1.5">
              <input type="file" accept=".pdf,image/*,.doc,.docx" onChange={onFile} className="text-sm" data-testid="cand-cv-input"/>
              {form.cv_filename && <div className="text-xs text-[var(--forest)] mt-2 flex items-center gap-1"><Upload className="w-3 h-3"/> {form.cv_filename}</div>}
            </div>
          </div>
          <div className="md:col-span-2"><label className="text-sm font-medium">Observações</label><textarea value={form.observations} onChange={update("observations")} className={`mt-1.5 ${inputCls} min-h-[60px]`} data-testid="cand-observations-input"/></div>
        </div>
        <button disabled={busy} className="pill-btn pill-primary w-full justify-center" data-testid="cand-submit-btn">
          {busy ? "A enviar…" : "Enviar recomendação"}
        </button>
      </form>
    </div>
  );
}
